// App.js - Main entry point with routing
// All routes for customer and provider flows
// ProviderOnboarding added for new caretaker signup flow
// ProtectedRoute guards authenticated and role-specific pages

import React, { useState, useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { auth, db } from './firebase';
import { doc, getDoc } from 'firebase/firestore';
import Signup from './components/Signup';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Booking from './components/Booking';
import Payment from './components/Payment';
import ProviderSignup from './components/ProviderSignup';
import ProviderDashboard from './components/ProviderDashboard';
import ProviderOnboarding from './components/ProviderOnboarding';
import IndependentContractorAgreement from './components/IndependentContractorAgreement';
import HandbookAcknowledgment from './components/HandbookAcknowledgment';
import BookingSuccess from './components/BookingSuccess';
import NotFound from './components/NotFound';
import VerifyEmail from './components/VerifyEmail';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import Contact from './components/Contact';
import CustomerFAQ from './components/CustomerFAQ';
import CaretakerFAQ from './components/CaretakerFAQ';
import OutOfArea from './components/OutOfArea';
import TermsAcknowledge from './components/TermsAcknowledge';
import TermsAcknowledgeProvider from './components/TermsAcknowledgeProvider';
import AdminDashboard from './components/AdminDashboard';
import RefundPolicy from './components/RefundPolicy';
import useScrollAnimation from './hooks/useScrollAnimation';
import { Analytics } from '@vercel/analytics/react';

// ========== PROTECTED ROUTE ==========
// Checks auth state and user role before rendering a protected page.
// - Unauthenticated users → /login
// - Wrong-role users → their correct dashboard
// - Shows a brief loading spinner while auth is being verified
function ProtectedRoute({ children, role }) {
  const [status, setStatus] = useState('checking'); // 'checking' | 'ok' | 'unauthed'
  const [userRole, setUserRole] = useState(null);
  const [emailVerified, setEmailVerified] = useState(true);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setStatus('unauthed');
        return;
      }

      // Reload to get fresh emailVerified — cached token can be stale after verification
      try {
        await user.reload();
      } catch {
        // fall through if offline
      }
      const freshUser = auth.currentUser;
      const verified = freshUser ? freshUser.emailVerified : false;
      setEmailVerified(verified);

      // Fetch role from Firestore — set status only AFTER role is confirmed
      // to prevent a render window where status='ok' but userRole is still null
      let fetchedRole = null;
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        fetchedRole = snap.exists() ? snap.data().role : null;
      } catch {
        // If a specific role is required and we can't verify it, redirect to login.
        // Failing open here would let a customer reach a provider-only route
        // during a Firestore outage. If no role is required, fail open is fine.
        if (role) {
          setStatus('unauthed');
          return;
        }
      }

      console.log('Fetched role from Firestore:', fetchedRole, 'Required role:', role);
      setUserRole(fetchedRole);
      setStatus('ok');
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Still loading
  if (status === 'checking') {
    return (
      <div className="auth-gate-loading">
        <div className="loading-spinner" />
      </div>
    );
  }

  // Not logged in
  if (status === 'unauthed') {
    return <Navigate to="/login" replace />;
  }

  // Wrong role — redirect to correct dashboard
  if (role && userRole && userRole !== role) {
    return <Navigate to={userRole === 'provider' ? '/provider/dashboard' : '/dashboard'} replace />;
  }

  // Email not verified
  if (role && userRole === role && !emailVerified) {
    return <Navigate to="/verify-email" replace />;
  }

  // Role required but userRole still null — keep showing spinner rather than
  // rendering children or firing a wrong redirect
  if (role && !userRole) {
    return (
      <div className="auth-gate-loading">
        <div className="loading-spinner" />
      </div>
    );
  }

  console.log('ProtectedRoute state:', {
    status,
    userRole,
    role,
    emailVerified,
    path: window.location.pathname
  });
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <div className="app">
        <Routes>
          {/* ========== PUBLIC CUSTOMER ROUTES ========== */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/login" element={<Login />} />

          {/* ========== PROTECTED CUSTOMER ROUTES ========== */}
          <Route path="/dashboard" element={<ProtectedRoute role="customer"><Dashboard /></ProtectedRoute>} />
          <Route path="/book" element={<ProtectedRoute role="customer"><Booking /></ProtectedRoute>} />
          <Route path="/payment" element={<ProtectedRoute role="customer"><Payment /></ProtectedRoute>} />
          <Route path="/booking-success" element={<ProtectedRoute role="customer"><BookingSuccess /></ProtectedRoute>} />

          {/* ========== PUBLIC PROVIDER ROUTES ========== */}
          <Route path="/provider/signup" element={<ProviderSignup />} />

          {/* ========== PROTECTED PROVIDER ONBOARDING ==========
              Requires auth — new providers are signed in immediately after
              createUserWithEmailAndPassword in ProviderSignup.js so this works.
              Prevents unauthenticated users from loading the onboarding UI. */}
          <Route path="/provider/onboarding" element={<ProtectedRoute role="provider"><ProviderOnboarding /></ProtectedRoute>} />

          {/* ========== PROVIDER TERMS ACKNOWLEDGMENT ==========
              Shown after onboarding, before the contractor agreement.
              Providers have verified email before reaching this point so
              ProtectedRoute role="provider" is safe here. */}
          <Route path="/terms-acknowledge-provider" element={<ProtectedRoute role="provider"><TermsAcknowledgeProvider /></ProtectedRoute>} />

          {/* ========== PROVIDER AGREEMENT ==========
              Shown after terms acknowledgment — must be signed before
              the provider can access /provider/handbook or /provider/dashboard */}
          <Route path="/provider/agreement" element={<ProtectedRoute role="provider"><IndependentContractorAgreement /></ProtectedRoute>} />

          {/* ========== PROVIDER HANDBOOK ==========
              Shown after the contractor agreement — provider must scroll and
              acknowledge before reaching /provider/dashboard */}
          <Route path="/provider/handbook" element={<ProtectedRoute role="provider"><HandbookAcknowledgment /></ProtectedRoute>} />

          {/* ========== PROTECTED PROVIDER ROUTES ========== */}
          <Route path="/provider/dashboard" element={<ProtectedRoute role="provider"><ProviderDashboard /></ProtectedRoute>} />

          {/* ========== EMAIL VERIFICATION ROUTE ========== */}
          <Route path="/verify-email" element={<VerifyEmail />} />

          {/* ========== LEGAL & SUPPORT ROUTES ========== */}
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/contact" element={<Contact />} />

          {/* ========== FAQ ROUTES (public) ========== */}
          <Route path="/faq" element={<CustomerFAQ />} />
          <Route path="/caretaker-faq" element={<CaretakerFAQ />} />

          {/* ========== OUT OF AREA (public) ==========
              No auth guard — OutOfArea.js checks auth internally
              and loads the user's profile to pre-fill the request form */}
          <Route path="/out-of-area" element={<OutOfArea />} />

          {/* ========== TERMS ACKNOWLEDGMENT (public / no ProtectedRoute) ==========
              New customers are sent here immediately after signup, before email
              verification. DocumentAcknowledgment handles its own auth check. */}
          <Route path="/terms-acknowledge" element={<TermsAcknowledge />} />

          {/* ========== ADMIN ROUTE ==========
              Auth + role check handled internally — only role: 'admin' users see content.
              No ProtectedRoute wrapper since 'admin' is not a standard customer/provider role. */}
          <Route path="/admin" element={<AdminDashboard />} />

          {/* ========== 404 ROUTE ==========
              Catches any URL that doesn't match above
              Always keep this last */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
      <Analytics />
    </BrowserRouter>
  );
}

// ========== LANDING PAGE ==========
function LandingPage() {
  // useScrollAnimation watches all .fade-in-up elements
  // and adds .visible class when they enter the viewport
  useScrollAnimation();

  // Track scroll position to condense navbar on mobile when user scrolls past 60px
  const [navScrolled, setNavScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => setNavScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="landing">

      {/* ========== NAVBAR ==========
          On mobile, navbar-scrolled class hides nav links and compacts the bar
          On desktop this class has no visual effect */}
      <nav className={`navbar ${navScrolled ? 'navbar-scrolled' : ''}`}>
        <h1 className="logo">Evermark</h1>
        <div className="nav-links">
          <a href="#how-it-works" className="nav-link-hide-scrolled">How It Works</a>
          <a href="#pricing" className="nav-link-hide-scrolled">Pricing</a>
          <a href="/provider/signup" className="provider-link nav-link-hide-scrolled">Become a Caretaker</a>
          <a href="/signup">
            <button className="btn-primary">Get Started</button>
          </a>
        </div>
      </nav>

      {/* ========== HERO SECTION ========== */}
      <section className="hero" style={{ position: 'relative' }}>
        <div className="hero-badge">Trusted Grave Care Nationwide</div>
        <h2>
          Honor Your Loved Ones,<br />
          <em>From Anywhere</em>
        </h2>
        <p>
          We connect you with trusted local caretakers who visit, tend,
          and photograph your loved one's grave — so you never have to wonder
          if they're being remembered.
        </p>
        <div className="hero-buttons">
          <a href="/signup">
            <button className="btn-primary btn-large">Book a Visit</button>
          </a>
          <a href="#how-it-works" className="btn-hero-secondary">
            See How It Works
          </a>
        </div>
        <p className="hero-serving-note">Now serving Mahoning &amp; Trumbull County, OH and Lawrence County, PA 🌿</p>
        <div className="hero-scroll-hint">
          <span>↓</span>
          <span>Scroll to learn more</span>
        </div>
      </section>

      {/* ========== TRUST BAR ========== */}
      <div className="trust-bar">
        <div className="trust-item fade-in-up delay-1">
          <span className="trust-icon">📸</span>
          <span>Photo proof after every visit</span>
        </div>
        <div className="trust-item fade-in-up delay-2">
          <span className="trust-icon">🛡️</span>
          <span>Local trusted caretakers</span>
        </div>
        <div className="trust-item fade-in-up delay-3">
          <span className="trust-icon">✓</span>
          <span>Cancel anytime</span>
        </div>
      </div>

      {/* ========== HOW IT WORKS ========== */}
      <section className="how-it-works" id="how-it-works">
        <span className="section-label fade-in-up">Simple Process</span>
        <h2 className="fade-in-up">How Evermark Memorial Works</h2>
        <p className="section-subtitle fade-in-up">
          From booking to photo delivery in three simple steps
        </p>
        <div className="steps">
          <div className="step fade-in-up delay-1">
            <div className="step-number">1</div>
            <h3>Book a visit online in minutes</h3>
            <p>
              Enter the cemetery location, your loved one's name,
              and choose the service that feels right.
            </p>
          </div>
          <div className="step fade-in-up delay-2">
            <div className="step-number">2</div>
            <h3>A local caretaker tends to the grave</h3>
            <p>
              A trusted local caretaker in our network accepts your
              job and visits the cemetery on your behalf.
            </p>
          </div>
          <div className="step fade-in-up delay-3">
            <div className="step-number">3</div>
            <h3>Receive photos and a visit report</h3>
            <p>
              Timestamped before and after photos are delivered
              directly to your dashboard so you can see every detail.
            </p>
          </div>
        </div>
      </section>

      {/* ========== EMOTIONAL QUOTE SECTION ========== */}
      <section className="quote-section">
        <blockquote className="fade-in-up">
          "The peace of knowing someone is there,
          even when you can't be — that's everything."
        </blockquote>
        <p className="quote-author fade-in-up delay-1">
          — An Evermark Memorial customer, Youngstown, Ohio
        </p>
      </section>

      {/* ========== PRICING SECTION ========== */}
      <section className="pricing" id="pricing">
        <span className="section-label fade-in-up">Transparent Pricing</span>
        <h2 className="fade-in-up">One Simple Price. $59.</h2>
        <p className="section-subtitle fade-in-up">Headstone cleaning, respectful care, peace of mind.</p>

        {/* ========== FLAT BASE PRICE HERO ========== */}
        <div className="lp-base-price-card fade-in-up">
          <div className="lp-base-price-left">
            <span className="lp-base-price-label">Every Visit Includes</span>
            <ul className="lp-base-price-list">
              <li>✓ Professional headstone cleaning</li>
              <li>✓ Debris and overgrowth removal</li>
              <li>✓ Respectful, dignified care</li>
              <li>✓ Visit confirmation sent to you</li>
            </ul>
          </div>
          <div className="lp-base-price-right">
            <span className="lp-base-price-amount">$59</span>
            <span className="lp-base-price-sub">per visit</span>
            <a href="/signup"><button className="btn-primary">Book Now</button></a>
          </div>
        </div>

        {/* ========== ADD-ONS — grouped by category, display only ========== */}
        <p className="pricing-group-title fade-in-up" style={{ marginTop: '48px' }}>Make It More Meaningful</p>
        <p className="section-subtitle fade-in-up" style={{ marginBottom: '32px' }}>Add any of these extras when you book</p>

        {[
          { category: 'Flowers', items: [
            { label: 'Fresh Cut Bouquet',        desc: 'Fresh cut flowers placed beautifully at the site',                   price: '$20' },
            { label: 'Planted Seasonal Flowers', desc: 'Seasonal flowers planted at the grave site',                         price: '$35' },
            { label: 'Flower Bed Refresh',       desc: 'Fresh mulch, light bed grooming, and weed touch-up',                 price: '$15' },
            { label: 'Flowers + Refresh Bundle', desc: 'Planted seasonal flowers plus full bed refresh — save $10',          price: '$40' },
          ]},
          { category: 'Decorations', items: [
            { label: 'Seasonal Decoration',        desc: 'Wreath, yard stakes, or seasonal display',            price: '$20' },
            { label: 'Birthday Remembrance Setup', desc: 'Balloon stakes and flowers for a birthday tribute',   price: '$20' },
            { label: 'Bow / Ribbon',               desc: 'Decorative bow or ribbon placed at the site',         price: '$10' },
            { label: 'Solar Memorial Light',       desc: 'Solar light installed at the grave site',             price: '$18' },
          ]},
          { category: 'Flags', items: [
            { label: 'Veteran / Holiday Flag', desc: 'Small flag placed with honor at the site',   price: '$8'  },
            { label: 'Full Flag Kit',          desc: 'Full flag display kit placed at the site',   price: '$18' },
          ]},
          { category: 'Care', items: [
            { label: 'Heavy Overgrowth Clearing', desc: 'Removal of heavy overgrowth around the grave site',                  price: '$25' },
            { label: 'Additional Grave',          desc: 'Full service cleaning for one additional grave at the same cemetery', price: '$30' },
            { label: 'Extended Visit',            desc: 'Extra 30 minutes of dedicated care',                                 price: '$15' },
          ]},
          { category: 'Media', items: [
            { label: 'Video Update',          desc: 'A short video of the completed visit sent to you', price: '$15' },
            { label: 'Written Visit Report',  desc: 'Detailed written report of the visit',            price: '$10' },
          ]},
        ].map(({ category, items }) => (
          <div key={category} className="lp-addon-category fade-in-up">
            <p className="lp-addon-category-title">{category}</p>
            <div className="lp-addon-grid">
              {items.map(addon => (
                <div key={addon.label} className="lp-addon-card">
                  <div className="lp-addon-info">
                    <span className="lp-addon-label">{addon.label}</span>
                    <span className="lp-addon-desc">{addon.desc}</span>
                  </div>
                  <span className="lp-addon-price">+{addon.price}</span>
                </div>
              ))}
            </div>
          </div>
        ))}


      </section>

      {/* ========== FOOTER ========== */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-brand fade-in-up">
            <h3>Evermark Memorial</h3>
            <p>Evermark Memorial — Caring for memories, near and far.</p>
          </div>
          <div className="footer-links fade-in-up delay-1">
            <h4>Services</h4>
            <ul>
              <li><a href="/signup">Book a Visit</a></li>
              <li><a href="#pricing">Pricing</a></li>
              <li><a href="/provider/signup">Become a Caretaker</a></li>
            </ul>
          </div>
          <div className="footer-links fade-in-up delay-2">
            <h4>Company</h4>
            <ul>
              <li><a href="#how-it-works">How It Works</a></li>
              <li><a href="/contact">Contact</a></li>
            </ul>
          </div>
          <div className="footer-links fade-in-up delay-3">
            <h4>Legal</h4>
            <ul>
              <li><a href="/terms">Terms of Service</a></li>
              <li><a href="/privacy">Privacy Policy</a></li>
              <li><a href="/refund-policy">Refund Policy</a></li>
            </ul>
          </div>
          <div className="footer-links fade-in-up delay-3">
            <h4>Help</h4>
            <ul>
              <li><a href="/faq">Customer FAQ</a></li>
              <li><a href="/caretaker-faq">Caretaker FAQ</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <p>© 2026 Evermark Memorial. All rights reserved. Made with care.</p>
        </div>
      </footer>

    </div>
  );
}

export default App;
